import {
  SVGProps,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  FunctionComponent,
} from 'react';
import { ReactSVG } from 'react-svg';
import { twMerge } from 'src/utils/twMerge';
import { cdnImage } from 'src/utils/cdn';
import { reportError } from 'src/modules/logs/Sentry';

const FailedFallback = () => <span data-cdn-icon-failed="" />;

type PlaceholderProps = {
  className?: string;
  width?: number | string;
  height?: number | string;
  style?: React.CSSProperties;
};

const injectedStyle = (style?: React.CSSProperties) =>
  style &&
  Object.fromEntries(
    Object.entries(style).filter(([key]) => key === key.toLowerCase()),
  );

const IconPlaceholder: FunctionComponent<PlaceholderProps> = ({
  className,
  width,
  height,
  style,
}) => (
  <span
    data-cdn-icon-loading=""
    className={twMerge('inline-block', className)}
    style={{
      ...injectedStyle(style),
      width: width ?? style?.width,
      height: height ?? style?.height,
    }}
  />
);

const useStableCallback = (callback?: () => void) => {
  const ref = useRef(callback);
  useEffect(() => {
    ref.current = callback;
  }, [callback]);
  return useCallback(() => ref.current?.(), []);
};

let cdnIconScopeSeq = 0;

let hasReportedLoadFailure = false;

export const __resetCdnIconFailureReportForTest = (): void => {
  hasReportedLoadFailure = false;
};

// A device that cannot reach the CDN fails every icon on every mount: one event per session is enough.
const reportLoadFailure = (error: unknown, name: string) => {
  if (hasReportedLoadFailure) return;
  hasReportedLoadFailure = true;
  reportError(
    '[CdnIcon] Error loading SVG',
    { error, name },
    { fingerprint: ['cdn-icon-load-failed'] },
  );
};

// react-svg owns the div around the injected svg; Safari < 15.4 cannot target it with :has().
const markIconWrapper = (svg: SVGSVGElement) => {
  svg.parentElement?.classList.add('cdn-svg-icon-inner');
};

const useAfterInjection = (notifyLoad: () => void) =>
  useCallback(
    (svg: SVGSVGElement) => {
      markIconWrapper(svg);
      notifyLoad();
    },
    [notifyLoad],
  );

export const scopeSvgStyle = (
  css: string,
  prevId: string,
  uid: string,
): string => {
  const noComments = css.replace(/\/\*[\s\S]*?\*\//g, '');
  const rebased = prevId
    ? noComments.split(`#${prevId}`).join(`#${uid}`)
    : noComments;
  // Nested at-rules are left alone: prefixing would mangle nested braces.
  if (rebased.includes('@')) {
    return rebased;
  }
  const scope = `svg#${uid}`;
  return rebased.replace(
    /([^{}]+)\{([^{}]*)\}/g,
    (_full, selectorList: string, body: string) =>
      `${selectorList
        .split(',')
        .map(raw => raw.trim())
        .filter(Boolean)
        .map(sel => (sel.includes(`#${uid}`) ? sel : `${scope} ${sel}`))
        .join(', ')}{${body.trim()}}`,
  );
};

const scopeInjectedSvgStyles = (svg: SVGSVGElement) => {
  const styleEls = svg.querySelectorAll('style');
  if (styleEls.length === 0) {
    return;
  }
  const prevId = svg.id;
  const uid = `cdnicon-${++cdnIconScopeSeq}`;
  svg.id = uid;
  styleEls.forEach(el => {
    el.textContent = scopeSvgStyle(el.textContent ?? '', prevId, uid);
  });
};

type Props = {
  name: string;
  className?: string;
  style?: React.CSSProperties;
  width?: number | string;
  height?: number | string;
  'data-testid'?: string;
  containerClassName?: string;
  onClick?: () => void;
  disableStyleScoping?: boolean;
  renumerateIRIElements?: boolean;
  onLoaded?: () => void;
};

const CdnIcon: FunctionComponent<Props> = ({
  name,
  className,
  style,
  width,
  height,
  'data-testid': dataTestId,
  containerClassName,
  onClick,
  disableStyleScoping,
  renumerateIRIElements,
  onLoaded,
}) => {
  const notifyLoad = useStableCallback(onLoaded);
  const afterInjection = useAfterInjection(notifyLoad);
  const beforeInjection = useCallback(
    (svg: SVGSVGElement) => {
      if (className) {
        svg.setAttribute('class', className);
      }
      if (width !== undefined) {
        svg.setAttribute('width', String(width));
      }
      if (height !== undefined) {
        svg.setAttribute('height', String(height));
      }
      if (style) {
        Object.entries(style).forEach(([key, value]) => {
          svg.style.setProperty(key, value as string);
        });
      }
      if (!disableStyleScoping) {
        scopeInjectedSvgStyles(svg);
      }
    },
    [className, disableStyleScoping, height, style, width],
  );

  const onSvgError = useCallback(
    (error: unknown) => {
      reportLoadFailure(error, name);
      notifyLoad();
    },
    [name, notifyLoad],
  );

  const LoadingFallback = useMemo(
    () => () => (
      <IconPlaceholder
        className={className}
        width={width}
        height={height}
        style={style}
      />
    ),
    [className, height, style, width],
  );

  return (
    <ReactSVG
      renumerateIRIElements={renumerateIRIElements ?? true}
      data-testid={dataTestId}
      className={`${containerClassName ?? ''} cdn-svg-icon`}
      src={cdnImage(name)}
      beforeInjection={beforeInjection}
      afterInjection={afterInjection}
      wrapper="div"
      loading={LoadingFallback}
      fallback={FailedFallback}
      onError={onSvgError}
      onClick={onClick}
    />
  );
};

export type CdnIconComponentProps = Omit<SVGProps<SVGSVGElement>, 'onClick'> & {
  containerClassName?: string;
  'data-testid'?: string;
  onClick?: React.MouseEventHandler;
  renumerateIRIElements?: boolean;
  disableStyleScoping?: boolean;
  onLoaded?: () => void;
};

const cdnIconsByName = new Map<string, FunctionComponent<CdnIconComponentProps>>();

export const createCdnIcon = (name: string): FunctionComponent<CdnIconComponentProps> => {
  const cached = cdnIconsByName.get(name);
  if (cached) return cached;
  const src = cdnImage(name);

  const IconComponent: FunctionComponent<CdnIconComponentProps> = ({
    className,
    style,
    width,
    height,
    containerClassName,
    onClick,
    'data-testid': dataTestId,
    renumerateIRIElements,
    disableStyleScoping,
    onLoaded,
  }) => {
    const notifyLoad = useStableCallback(onLoaded);
    const afterInjection = useAfterInjection(notifyLoad);
    const handleError = useCallback(
      (error: unknown) => {
        reportLoadFailure(error, name);
        notifyLoad();
      },
      [notifyLoad],
    );
    const beforeInjection = useCallback(
      (svg: SVGSVGElement) => {
        if (className) {
          svg.setAttribute('class', className);
        }
        if (width !== undefined) {
          svg.setAttribute('width', String(width));
        }
        if (height !== undefined) {
          svg.setAttribute('height', String(height));
        }
        if (style) {
          Object.entries(style).forEach(([key, value]) => {
            svg.style.setProperty(key, value as string);
          });
        }
        if (!disableStyleScoping) {
          scopeInjectedSvgStyles(svg);
        }
      },
      [className, width, height, style, disableStyleScoping],
    );

    const Placeholder = useMemo(
      () => () => (
        <IconPlaceholder
          className={className}
          width={width}
          height={height}
          style={style}
          />
      ),
      [className, height, style, width],
    );

    const FailedPlaceholder = useMemo(
      () => () => <span className={className} data-cdn-icon-failed="" />,
      [className],
    );

    return (
      <ReactSVG
        renumerateIRIElements={renumerateIRIElements ?? true}
        src={src}
        beforeInjection={beforeInjection}
        afterInjection={afterInjection}
        data-testid={dataTestId}
        className={`${containerClassName ?? ''} cdn-svg-icon`}
        wrapper="div"
        loading={Placeholder}
        fallback={FailedPlaceholder}
        onError={handleError}
        onClick={onClick}
      />
    );
  };
  IconComponent.displayName = `CdnIcon(${name})`;
  cdnIconsByName.set(name, IconComponent);
  return IconComponent;
};

export default CdnIcon;
